import api from 'services/api';
import { REJECT, array, num, numeric, object, refuseDegraded, str } from 'validation';
import { readResponse } from 'api/shared/readResponse';

// A PROJECTION CONTRACT over `GET /locks` — see ../../README.md. Self-contained on
// purpose: its own type, its own schema, shared with nothing, including with
// `searchLocks`, which asks a different question of the same route and would stop
// projecting the same fields the moment either changed.
//
// The full contract is `getLocks`, which declares seventeen fields because the list
// page reads them. This reads three: the two the stored `zone_name|name` path is
// built from, plus the id, which the paging below dedups on. `object()` ignores the
// rest, so nothing else is parsed and nothing else can warn — and that stays true
// when `getLocks` grows in its E-S pass.
//
// `GET /locks` defaults to 50 rows and enforces no maximum on `limit`, so one large
// page covers every installation seen so far (largest reported: ~105 locks). The
// loop is the safety net, not the normal path.
const PAGE_SIZE = 2000;

/** A lock as a picker deals in it, plus the id paging dedups on. */
export interface LockSummary {
  readonly id: number;
  readonly name: string;
  readonly zone_name: string;
}

const summarySchema = object<LockSummary>({
  id: num(REJECT),
  name: str(REJECT),
  zone_name: str(),
});

interface LocksPage {
  readonly locks: readonly LockSummary[];
  readonly pagination: { readonly total_records: number };
}

const responseSchema = object<LocksPage>({
  locks: array(summarySchema),
  // COUNT-derived totals arrive as numeric strings from Postgres.
  pagination: object<{ readonly total_records: number }>({ total_records: numeric(REJECT) }),
});

/**
 * Every lock the current user may list, following pagination to exhaustion.
 *
 * The node-definition editor's lock pickers filter and sort client-side, so a lock
 * absent from the response is a lock the user cannot pick at all — and the flow
 * editor's picker, which narrows to the flow's own zone, reports "no locks left"
 * rather than a short list when a truncated page happens to hold none of that
 * zone's locks. Both need the complete set.
 *
 * Which is why it refuses a degraded page, and why that is a property of THIS
 * contract rather than of its current callers: something called `getAll` that
 * quietly returns some has broken its own promise, whoever is asking. A caller that
 * can live with a partial list wants a different read, not a laxer collapse here.
 */
export const getAllLocks = async (): Promise<readonly LockSummary[]> => {
  const rows: LockSummary[] = [];
  const seen = new Set<number>();
  let fetched = 0;
  let total = 0;

  do {
    const res = await api.get('/locks', { params: { limit: PAGE_SIZE, offset: fetched } });
    const page = refuseDegraded(readResponse(res.data, { schema: responseSchema, source: 'getAllLocks' }));

    // An empty page means the server has no more to give, whatever its total
    // claims — stop rather than spin. Every other path advances `fetched`, so the
    // loop always terminates.
    if (page.locks.length === 0) {
      break;
    }

    fetched += page.locks.length;
    total = page.pagination.total_records;

    // Offset paging over a table that can gain rows mid-sequence may repeat a row
    // across pages; `id` keeps the result a set, so no duplicate reaches a consumer
    // that keys a list on it.
    for (const lock of page.locks) {
      if (!seen.has(lock.id)) {
        seen.add(lock.id);
        rows.push(lock);
      }
    }
  } while (fetched < total);

  return rows;
};
